<template>
  <div
    class="modal fade"
    tabIndex="-1"
    data-bs-backdrop="static"
    ref="modal"
  >
    <div
      class="modal-dialog modal-lg modal-dialog-scrollable"
      role="document"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEditMode ? $t("settings.settings") : $t("settings.new_tool") }}
          </h5>
        </div>
        <div class="modal-body">
          <label
            for="tool-name"
            class="form-label"
          >
            {{ $t("settings.name") }}
          </label>
          <div class="input-group mb-3">
            <input
              class="form-control"
              id="tool-name"
              v-model="formData.name"
            />
          </div>

          <label
            for="tool-description"
            class="form-label"
          >
            {{ $t("settings.descriptionLabel") }}
          </label>
          <div class="input-group mb-3">
            <textarea
              class="form-control"
              id="tool-description"
              v-model="formData.description"
              rows="2"
            ></textarea>
          </div>

          <label
            for="tool-url"
            class="form-label"
          >
            {{ $t("settings.url") }}
          </label>
          <div class="input-group mb-3">
            <input
              class="form-control"
              id="tool-url"
              v-model="formData.url"
            />
          </div>

          <div class="mb-3">
            <label
              class="form-label"
              for="tool-icon"
            >
              {{ $t("settings.icon") }}
            </label>
            <div class="input-group">
              <input
                type="file"
                class="form-control"
                id="tool-icon"
                accept=".svg"
                @change="$emit('upload-icon', $event)"
              />
              <label
                v-if="formData.iconName"
                class="input-group-text"
                for="tool-icon"
              >
                {{ formData.iconName }}
              </label>
            </div>
            <small>.svg</small>
            <ImageBlob
              v-if="formData.iconPath"
              style="height: 200px"
              backgroundSize="contain"
              :imagePath="formData.iconPath"
            />
          </div>

          <div class="mb-3">
            <label
              class="form-label"
              for="tool-preview"
            >
              {{ $t("settings.preview") }}
            </label>
            <div class="input-group">
              <input
                type="file"
                class="form-control"
                id="tool-preview"
                accept=".gif, .jpg, .png"
                @change="$emit('upload-image', $event)"
              />
              <label
                v-if="formData.imageName"
                class="input-group-text"
                for="tool-preview"
              >
                {{ formData.imageName }}
              </label>
            </div>
            <small>.png, .jpg, .gif</small>
            <ImageBlob
              v-if="formData.imagePath"
              style="height: 200px"
              backgroundSize="contain"
              :imagePath="formData.imagePath"
            />
          </div>

          <label
            for="tool-token"
            class="form-label"
          >
            {{ $t("settings.admin_key") }}
          </label>
          <br />
          <small class="text-danger mt-0">
            {{ $t("settings.admin_key_message") }}
          </small>
          <div class="input-group mb-3">
            <textarea
              class="form-control"
              id="tool-token"
              v-model="formData.jsonToken"
              rows="3"
            ></textarea>
          </div>

          <label class="form-label">
            {{ $t("settings.claims") }}
          </label>
          <div
            class="input-group mb-3"
            v-for="(claim, index) in formData.claims"
            :key="'claim-input-' + index"
          >
            <div class="input-group-text">
              {{ $t("settings.default") }} &nbsp;
              <input
                class="form-check-input mt-0"
                type="checkbox"
                v-model="formData.claims[index].required"
                :aria-label="$t('settings.required')"
              />
            </div>
            <span class="input-group-text">{{ $t("settings.key") }}</span>
            <input
              type="text"
              :aria-label="$t('settings.key')"
              class="form-control"
              v-model="formData.claims[index].key"
            />
            <span class="input-group-text">{{ $t("settings.value") }}</span>
            <input
              type="text"
              :aria-label="$t('settings.value')"
              class="form-control"
              v-model="formData.claims[index].value"
            />
            <button
              type="button"
              class="btn btn-danger text-white"
              @click="deleteClaim(index)"
            >
              <font-awesome-icon
                class="text-white"
                :icon="['far', 'trash-xmark']"
              />
            </button>
          </div>

          <div>
            <button
              type="button"
              class="btn btn-primary text-white"
              @click="addClaim"
            >
              <font-awesome-icon
                class="text-white"
                :icon="['far', 'plus']"
              />
            </button>
          </div>

          <div class="mt-4">
            <label class="form-label d-flex align-items-center gap-2">
              <input
                class="form-check-input"
                type="checkbox"
                v-model="formData.maintenance.enabled"
              />
              <span>{{ $t("settings.maintenance_mode") }}</span>
            </label>

            <small
              v-if="formData.maintenance.enabled"
              class="text-muted d-block mb-2"
            >
              {{ $t("settings.maintenance_superadmin_hint") }}
            </small>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-outline-secondary"
            @click="hide"
          >
            {{ $t("settings.close") }}
          </button>
          <button
            type="button"
            class="btn btn-primary text-white"
            @click="save"
          >
            {{ isEditMode ? $t("settings.save") : $t("settings.create") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {Modal} from "@comlinedev/bootstrap5";
  import ImageBlob from "../smallComponents/ImageBlob.vue";

  export default {
    name: "ToolSettingsModal",
    components: {
      ImageBlob,
    },
    props: {
      isEditMode: {
        type: Boolean,
        default: false,
      },
      toolData: {
        type: Object,
        default: () => ({
          name: "",
          description: "",
          url: "",
          iconName: "",
          iconPath: "",
          imageName: "",
          imagePath: "",
          jsonToken: null,
          claims: [],
          maintenance: {
            enabled: false,
          },
        }),
      },
    },
    emits: ["save", "upload-icon", "upload-image"],
    data() {
      return {
        modalInstance: null,
        formData: {
          name: "",
          description: "",
          url: "",
          iconName: "",
          iconPath: "",
          imageName: "",
          imagePath: "",
          jsonToken: null,
          claims: [],
          maintenance: {
            enabled: false,
          },
        },
        formInitialized: false,
      };
    },
    watch: {
      toolData: {
        handler(newData) {
          if (!this.formInitialized || !this.isModalShown()) {
            this.formData = this.cloneToolData(newData);
            this.formInitialized = true;
            return;
          }

          this.formData.iconName = newData?.iconName || "";
          this.formData.iconPath = newData?.iconPath || "";
          this.formData.imageName = newData?.imageName || "";
          this.formData.imagePath = newData?.imagePath || "";
        },
        deep: true,
        immediate: true,
      },
    },
    methods: {
      cloneToolData(toolData) {
        const normalized = toolData || {};

        return {
          name: normalized.name || "",
          description: normalized.description || "",
          url: normalized.url || "",
          iconName: normalized.iconName || "",
          iconPath: normalized.iconPath || "",
          imageName: normalized.imageName || "",
          imagePath: normalized.imagePath || "",
          jsonToken: normalized.jsonToken || null,
          claims: Array.isArray(normalized.claims)
            ? normalized.claims.map((claim) => ({
                key: claim?.key || "",
                value: claim?.value || "",
                required: !!claim?.required,
              }))
            : [],
          maintenance: {
            enabled: !!normalized?.maintenance?.enabled,
          },
        };
      },
      isModalShown() {
        return !!this.modalInstance?._isShown;
      },
      show() {
        this.$nextTick(() => {
          this.formData = this.cloneToolData(this.toolData);
          this.formInitialized = true;
          this.modalInstance?.show();
        });
      },
      hide() {
        this.modalInstance?.hide();
      },
      addClaim() {
        this.formData.claims.push({key: "", value: "", required: false});
      },
      deleteClaim(index) {
        this.formData.claims.splice(index, 1);
      },
      save() {
        this.$emit("save", {...this.formData});
      },
    },
    mounted() {
      this.modalInstance = new Modal(this.$refs.modal);
    },
  };
</script>
